<script setup lang="ts">
import { computed } from 'vue';
import { useDialogPluginComponent, copyToClipboard, Notify } from 'quasar';
import {
  MESSAGE_VALIDATION_ISSUE_LABELS,
  MessageGateAction,
  type MessageValidationIssue,
} from './ticketsTypes.ts';

const props = defineProps<{
  original: string;
  improvedEn: string;
  translation: string;
  translationLanguage: string;
  issues: MessageValidationIssue[];
  foreignFragments: string[];
}>();

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const issueLabels = computed<string[]>(() =>
  props.issues.map((code) => MESSAGE_VALIDATION_ISSUE_LABELS[code] ?? code),
);

const hasTranslation = computed<boolean>(() => Boolean(props.translation?.trim()));

// "What it means (Russian)" / "(Polish)" — falls back to a generic label if the language is unknown.
const translationLabel = computed<string>(() =>
  props.translationLanguage?.trim()
    ? `What it means (${props.translationLanguage})`
    : 'What it means',
);

// "Send improved" ships the AI version; "Send as-is" ships the accountant's original text.
const sendImproved = (): void => onDialogOK({ action: MessageGateAction.Send, text: props.improvedEn });
const sendOriginal = (): void => onDialogOK({ action: MessageGateAction.Send, text: props.original });
// "Edit suggestion" loads the improved English into the input for further editing (not sent).
const editSuggestion = (): void => onDialogOK({ action: MessageGateAction.Edit, text: props.improvedEn });
// Closing keeps the accountant's original text in the input to edit.
const closeDialog = (): void => onDialogCancel();

const copySuggestion = async (): Promise<void> => {
  try {
    await copyToClipboard(props.improvedEn);
    Notify.create({ message: 'Copied to clipboard', color: 'positive', position: 'top' });
  } catch {
    Notify.create({ message: 'Failed to copy', color: 'negative', position: 'top' });
  }
};
</script>

<template>
  <q-dialog
    ref="dialogRef"
    @hide="onDialogHide"
  >
    <q-card class="message-validation-card column no-wrap">
      <q-card-section class="row items-center justify-between q-pb-sm">
        <div class="text-h6">
          Review before sending
        </div>
        <q-btn
          icon="close"
          color="primary"
          flat
          round
          dense
          @click="closeDialog"
        >
          <q-tooltip
            class="bg-grey-10 text-body1"
            max-width="280px"
          >
            Close and keep editing your original message. Nothing is sent.
          </q-tooltip>
        </q-btn>
      </q-card-section>

      <q-card-section
        v-if="issueLabels.length || foreignFragments.length"
        class="q-pt-none q-pb-sm"
      >
        <div class="row items-center q-gutter-xs">
          <q-chip
            v-for="label in issueLabels"
            :key="label"
            color="orange-2"
            text-color="orange-9"
            dense
            size="sm"
            icon="warning"
          >
            {{ label }}
          </q-chip>
          <!-- The exact words the AI found in another language. -->
          <q-chip
            v-for="(fragment, index) in foreignFragments"
            :key="`${fragment}-${index}`"
            color="grey-3"
            text-color="grey-9"
            dense
            size="sm"
          >
            {{ fragment }}
          </q-chip>
        </div>
        <div
          v-if="foreignFragments.length"
          class="text-caption text-grey-7 q-mt-xs"
        >
          The client cannot read these words. They are translated in the version below.
        </div>
      </q-card-section>

      <q-separator />

      <q-card-section class="col scroll column no-wrap q-gutter-y-md">
        <!-- Accountant's original draft -->
        <div>
          <div class="text-caption text-grey-7 q-mb-xs">
            Your message
          </div>
          <div class="validation-block validation-block--muted">
            {{ original }}
          </div>
        </div>

        <!-- Improved English — what will actually be sent -->
        <div>
          <div class="row items-center justify-between q-mb-xs">
            <div class="text-caption text-primary text-weight-medium">
              Will be sent (English)
            </div>
            <q-btn
              icon="content_copy"
              flat
              round
              dense
              size="sm"
              color="primary"
              @click="copySuggestion"
            >
              <q-tooltip
                class="bg-grey-10 text-body1"
                max-width="280px"
              >
                Copy the English version to the clipboard.
              </q-tooltip>
            </q-btn>
          </div>
          <div class="validation-block validation-block--primary">
            {{ improvedEn }}
          </div>
        </div>

        <!-- Back-translation into the accountant's source language — comprehension aid -->
        <div v-if="hasTranslation">
          <div class="text-caption text-grey-7 q-mb-xs">
            {{ translationLabel }}
          </div>
          <div class="validation-block validation-block--translation">
            {{ translation }}
          </div>
        </div>
      </q-card-section>

      <q-separator />

      <q-card-actions
        align="right"
        class="q-gutter-sm q-pa-md"
      >
        <q-btn
          label="Edit suggestion"
          icon="edit"
          flat
          no-caps
          @click="editSuggestion"
        >
          <q-tooltip
            class="bg-grey-10 text-body1"
            max-width="280px"
          >
            Put the suggested English version into the input so you can edit it. Nothing is sent yet.
          </q-tooltip>
        </q-btn>
        <q-btn
          label="Send as-is"
          flat
          no-caps
          color="grey-8"
          @click="sendOriginal"
        >
          <q-tooltip
            class="bg-grey-10 text-body1"
            max-width="280px"
          >
            Use your original text, unchanged.
          </q-tooltip>
        </q-btn>
        <q-btn
          label="Send improved"
          icon="auto_awesome"
          unelevated
          no-caps
          color="secondary"
          @click="sendImproved"
        >
          <q-tooltip
            class="bg-grey-10 text-body1"
            max-width="280px"
          >
            Use the suggested English version shown above.
          </q-tooltip>
        </q-btn>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.message-validation-card {
  width: 560px;
  max-width: 90vw;
  max-height: 85vh;
}

.validation-block {
  white-space: pre-wrap;
  word-break: break-word;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
}

.validation-block--muted {
  background: #f5f5f5;
  color: #616161;
}

.validation-block--primary {
  background: #e8eaf6;
  color: #1a237e;
  font-weight: 500;
}

.validation-block--translation {
  background: #f1f8e9;
  color: #33691e;
}
</style>
